import React, { useState } from 'react';
import { 
  Home, Store, Handshake, BookOpen, Search, User, X, Heart, 
  ShoppingCart, Star, Menu, Sun, Moon, Plus, Minus,
  Eye, Share2, MapPin, ArrowLeft, CheckCircle, DollarSign
} from 'lucide-react';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [cart, setCart] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const toggleFavorite = (product) => {
    setFavorites(prev => {
      const isFavorite = prev.some(item => item.id === product.id);
      if (isFavorite) {
        return prev.filter(item => item.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const navItems = [
    { id: 'home', label: 'Accueil', icon: Home },
    { id: 'artisans', label: 'Artisans', icon: User },
    { id: 'crowdfunding', label: 'Crowdfunding', icon: Handshake },
    { id: 'formations', label: 'Formations', icon: BookOpen },
    { id: 'register', label: 'Inscription', icon: User },
    { id: 'login', label: 'Connexion', icon: User }
  ];

  const featuredProducts = [
    { 
      id: 1, 
      name: 'Panier en paille tressée', 
      artisan: 'Fatouma M. - Atar', 
      imageUrl: 'https://placehold.co/400x300/8B4513/FFFFFF?text=Panier+Paille',
      price: 1500, 
      originalPrice: 2000,
      rating: 5,
      description: 'Panier artisanal en paille naturelle, tressé à la main selon les techniques traditionnelles.',
      isNew: true
    },
    { 
      id: 2, 
      name: 'Tapis berbère authentique', 
      artisan: 'Aïcha B. - Nouakchott',
      imageUrl: 'https://placehold.co/400x300/8B0000/FFFFFF?text=Tapis+Berbère',
      price: 8500, 
      rating: 4,
      description: 'Magnifique tapis en laine pure, aux motifs géométriques traditionnels mauritaniens.'
    },
    { 
      id: 3, 
      name: 'Bijoux en argent massif', 
      artisan: 'Mariam D. - Chinguetti',
      imageUrl: 'https://placehold.co/400x300/C0C0C0/000000?text=Bijoux+Argent',
      price: 2500, 
      rating: 5,
      description: 'Collection de bijoux traditionnels en argent massif, ornés de motifs ancestraux.'
    },
    { 
      id: 4, 
      name: 'Poterie décorative', 
      artisan: 'Hawa T. - Oualata',
      imageUrl: 'https://placehold.co/400x300/CD853F/FFFFFF?text=Poterie',
      price: 3000, 
      rating: 4,
      description: 'Poterie artisanale aux couleurs vives, décorée selon les traditions de Oualata.'
    }
  ];

  const stats = [
    { number: '50+', label: 'Artisanes partenaires' },
    { number: '100+', label: 'Produits uniques' },
    { number: '10+', label: 'Villes couvertes' },
    { number: '95%', label: 'Satisfaction client' }
  ];

  // Projets de crowdfunding avec couleurs et pourcentages différents
  const crowdfundingProjects = [
    {
      id: 1,
      title: 'Atelier de Tissage Moderne',
      description: 'Équipement d\'un atelier de tissage pour 15 artisanes à Nouakchott',
      percentage: 45,
      color: 'from-green-500 to-green-700',
      bgColor: 'bg-green-600',
      raised: 45000,
      goal: 100000,
      backers: 127
    },
    {
      id: 2,
      title: 'Centre de Formation Artisanale',
      description: 'Construction d\'un centre de formation pour les jeunes artisanes',
      percentage: 78,
      color: 'from-blue-500 to-blue-700',
      bgColor: 'bg-blue-600',
      raised: 78000,
      goal: 100000,
      backers: 203
    },
    {
      id: 3,
      title: 'Coopérative de Poterie',
      description: 'Création d\'une coopérative de poterie traditionnelle à Oualata',
      percentage: 100,
      color: 'from-purple-500 to-purple-700',
      bgColor: 'bg-purple-600',
      raised: 85000,
      goal: 85000,
      backers: 156,
      completed: true
    },
    { 
      id: 4,
      title: 'Boutique en Ligne Collective',
      description: 'Plateforme e-commerce pour promouvoir l\'artisanat mauritanien',
      percentage: 23,
      color: 'from-orange-500 to-orange-700',
      bgColor: 'bg-orange-600',
      raised: 23000,
      goal: 100000,
      backers: 89
    }
  ];

  // Noms d'artisanes réels
  const artisans = [
    { name: 'Fatimata Mint Ahmed', specialty: 'Tissage traditionnel', city: 'Nouakchott' },
    { name: 'Mariem Mint Sidi', specialty: 'Bijouterie en argent', city: 'Chinguetti' },
    { name: 'Aïcha Mint Mohamed', specialty: 'Poterie décorative', city: 'Oualata' },
    { name: 'Khadija Mint Ould', specialty: 'Maroquinerie artisanale', city: 'Atar' },
    { name: 'Aminata Mint Baba', specialty: 'Vannerie en palmier', city: 'Rosso' },
    { name: 'Zeynab Mint Ali', specialty: 'Broderie traditionnelle', city: 'Kaédi' }
  ];

  const Header = () => (
    <header className={`sticky top-0 z-50 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-r from-blue-600 to-blue-800'} text-white shadow-lg`}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div 
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => setCurrentPage('home')}
          >
            <Store className="w-8 h-8" />
            <span className="text-2xl font-bold text-yellow-400">Mauracraft</span>
          </div>

          <nav className="hidden md:flex space-x-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all ${
                  currentPage === item.id 
                    ? 'bg-white text-blue-600 shadow-lg' 
                    : 'hover:bg-white/20'
                }`}
                onClick={() => {
                  setCurrentPage(item.id);
                  if (item.id === 'crowdfunding') {
                    setSelectedProject(null);
                  }
                }}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-semibold">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="flex items-center space-x-4">
            <button
              className="p-2 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <button
              className="p-2 rounded-full hover:bg-white/20 transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>

            <button
              className="relative p-2 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setCurrentPage('cart')}
            >
              <ShoppingCart className="w-5 h-5" />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </button>

            <button
              className="md:hidden p-2 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg mb-2 transition-all ${
                  currentPage === item.id 
                    ? 'bg-white text-blue-600' 
                    : 'hover:bg-white/20'
                }`}
                onClick={() => {
                  setCurrentPage(item.id);
                  setMobileMenuOpen(false);
                }}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-semibold">{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
  );

  const ProductCard = ({ product, index }) => {
    const isFavorite = favorites.some(item => item.id === product.id);

    return (
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group`}>
        <div className="relative overflow-hidden">
          <img 
            src={product.imageUrl} 
            alt={product.name} 
            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          <div className="absolute top-4 right-4 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button
              className={`p-2 rounded-full ${isFavorite ? 'bg-red-500' : 'bg-white'} text-gray-700 shadow-lg`}
              onClick={() => toggleFavorite(product)}
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current text-white' : ''}`} />
            </button>
            <button className="p-2 rounded-full bg-white text-gray-700 shadow-lg">
              <Eye className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-full bg-white text-gray-700 shadow-lg">
              <Share2 className="w-4 h-4" />
            </button>
          </div>

          {product.isNew && (
            <div className="absolute top-4 left-4 bg-gradient-to-r from-green-400 to-green-600 text-white px-3 py-1 rounded-full text-xs font-bold">
              Nouveau
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between mb-2">
            <h4 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-blue-600'}`}>
              {product.name}
            </h4>
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${i < product.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                />
              ))}
            </div>
          </div>
          
          <div className="flex items-center space-x-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-500" />
            <p className="text-sm text-gray-500">{product.artisan}</p>
          </div>

          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 line-clamp-2`}>
            {product.description}
          </p>

          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="font-bold text-2xl text-blue-600">{product.price} MRU</span>
              {product.originalPrice && (
                <span className="text-sm text-gray-500 line-through">{product.originalPrice} MRU</span>
              )}
            </div>
            <button
              className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all"
              onClick={() => addToCart(product)}
            >
              Ajouter
            </button>
          </div>
        </div>
      </div>
    );
  };

  const HomeView = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-orange-50'}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-blue-800/90" />
        
        <div className="relative container mx-auto px-4 py-20 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            L'artisanat mauritanien
            <br />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              réinventé
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Découvrez des créations uniques, soutenez les femmes artisanes et participez à la préservation d'un patrimoine millénaire.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all"
              onClick={() => setCurrentPage('artisans')}
            >
              Découvrir les Artisanes
            </button>
            
            <button
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-600 transition-all"
              onClick={() => setCurrentPage('crowdfunding')}
            >
              Soutenir un Projet
            </button>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:shadow-2xl transition-all duration-300`}
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <p className={`text-lg font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className={`text-4xl md:text-5xl font-bold text-center mb-4 ${darkMode ? 'text-white' : 'text-blue-600'}`}>
            Produits en Vedette
          </h2>
          
          <p className={`text-xl text-center mb-12 max-w-3xl mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Découvrez une sélection de créations exceptionnelles, chacune racontant l'histoire unique de son artisane.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all">
              Voir Tous les Produits
            </button>
          </div>
        </div>
      </section>
    </div>
  );

  const CartView = () => {
    const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
        <div className="container mx-auto max-w-4xl">
          <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8`}>
            Mon Panier
          </h1>

          {cart.length === 0 ? (
            <div className={`text-center py-16 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <ShoppingCart className="w-24 h-24 mx-auto mb-4 opacity-50" />
              <h2 className="text-2xl font-semibold mb-2">Votre panier est vide</h2>
              <p className="text-lg">Découvrez nos magnifiques créations artisanales</p>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-xl`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <img 
                        src={item.imageUrl} 
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div>
                        <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'}`}>
                          {item.name}
                        </h3>
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {item.artisan}
                        </p>
                        <p className="text-blue-600 font-bold text-lg">
                          {item.price} MRU × {item.quantity}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      className="bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition-colors"
                      onClick={() => removeFromCart(item.id)}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}

              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-xl`}>
                <div className="flex justify-between items-center mb-6">
                  <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'}`}>
                    Total:
                  </span>
                  <span className="text-3xl font-bold text-blue-600">
                    {total} MRU
                  </span>
                </div>
                
                <button className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all">
                  Procéder au Paiement
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const ArtisansView = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
      <div className="container mx-auto">
        <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8`}>
          Nos Artisanes
        </h1>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl`}>
          <h2 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-blue-600'} mb-4`}>
            Découvrez les Talents de Mauritanie
          </h2>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
            Rencontrez les femmes artisanes exceptionnelles qui perpétuent les traditions ancestrales mauritaniennes. 
            Chaque création raconte une histoire unique, transmise de génération en génération.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artisans.map((artisan, i) => (
              <div
                key={i}
                className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6 text-center`}
              >
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <User className="w-10 h-10 text-white" />
                </div>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-blue-600'} mb-2`}>
                  {artisan.name}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>
                  {artisan.specialty}
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {artisan.city}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const ProjectDetailView = ({ project }) => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
      <div className="container mx-auto max-w-4xl">
        <button
          className={`flex items-center space-x-2 mb-6 ${darkMode ? 'text-white' : 'text-blue-600'} hover:underline`}
          onClick={() => setSelectedProject(null)}
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Retour aux projets</span>
        </button>

        {project.completed ? (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl text-center`}>
            <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6" />
            <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-4`}>
              Projet Entièrement Financé !
            </h1>
            <h2 className={`text-2xl font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
              {project.title}
            </h2>
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
              Grâce à la générosité de {project.backers} contributeurs, ce projet a atteint son objectif de financement de {project.goal.toLocaleString()} MRU.
            </p>
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-6 mb-8`}>
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-green-800'} mb-4`}>
                Prochaines Étapes
              </h3>
              <ul className={`text-left ${darkMode ? 'text-gray-300' : 'text-green-700'} space-y-2`}>
                <li>• Démarrage des travaux dans les 2 prochaines semaines</li>
                <li>• Mise à jour régulière des contributeurs</li>
                <li>• Livraison prévue dans 3 mois</li>
                <li>• Invitation à l'inauguration pour tous les contributeurs</li>
              </ul>
            </div>
            <button
              className="bg-gradient-to-r from-green-500 to-green-700 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all"
              onClick={() => setCurrentPage('crowdfunding')}
            >
              Découvrir d'autres projets
            </button>
          </div>
        ) : (
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl`}>
            <div className={`w-full h-64 ${project.bgColor} rounded-lg mb-6 flex items-center justify-center`}>
              <Handshake className="w-24 h-24 text-white" />
            </div>
            
            <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-4`}>
              {project.title}
            </h1>
            
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
              {project.description}
            </p>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                  Progression du financement
                </span>
                <span className="font-bold text-blue-600">
                  {project.percentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className={`bg-gradient-to-r ${project.color} h-4 rounded-full transition-all duration-500`}
                  style={{ width: `${project.percentage}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                    {project.raised.toLocaleString()} MRU
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Collectés
                  </div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                    {project.goal.toLocaleString()} MRU
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Objectif
                  </div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                    {project.backers}
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Contributeurs
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-blue-600'}`}>
                Montants de contribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { amount: 1000, reward: 'Remerciement personnalisé' },
                  { amount: 5000, reward: 'Produit artisanal + remerciements' },
                  { amount: 10000, reward: 'Visite de l\'atelier + produits' }
                ].map((tier, i) => (
                  <div key={i} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4 text-center`}>
                    <div className="text-xl font-bold text-blue-600 mb-2">
                      {tier.amount} MRU
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {tier.reward}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 text-center">
              <button className={`bg-gradient-to-r ${project.color} text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all`}
                onClick={() => {
                  setSelectedProject(project);
                  setCurrentPage("contributionProcess");
                }}
              >
                Contribuer au Projet
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const ContributionProcessView = ({ project }) => {
    const [amount, setAmount] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      // Here you would typically send the data to a backend
      console.log('Contribution submitted:', { project: project.title, amount, name, email });
      setCurrentPage('contributionSuccess');
    };

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
        <div className="container mx-auto max-w-md">
          <button
            className={`flex items-center space-x-2 mb-6 ${darkMode ? 'text-white' : 'text-blue-600'} hover:underline`}
            onClick={() => setCurrentPage('crowdfunding')}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour aux projets</span>
          </button>

          <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8 text-center`}>
            Contribuer à {project.title}
          </h1>
          
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl`}>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Montant de la contribution (MRU)
                </label>
                <input
                  type="number"
                  className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                  placeholder="Ex: 5000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Votre nom
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                  placeholder="Votre nom complet"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Votre email
                </label>
                <input
                  type="email"
                  className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Confirmer la contribution
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const ContributionSuccessView = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8 flex items-center justify-center`}>
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl text-center max-w-md`}>
        <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6" />
        <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-4`}>
          Contribution Réussie !
        </h1>
        <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
          Merci pour votre généreuse contribution. Votre soutien est essentiel pour nos artisanes !
        </p>
        <button
          className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all"
          onClick={() => setCurrentPage('crowdfunding')}
        >
          Retour aux projets
        </button>
      </div>
    </div>
  );

  const CrowdfundingView = () => {
    if (selectedProject) {
      return <ProjectDetailView project={selectedProject} />;
    }

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
        <div className="container mx-auto">
          <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8`}>
            Projets Crowdfunding
          </h1>
          
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl mb-8`}>
            <h2 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-blue-600'} mb-4`}>
              Soutenez l'Innovation Artisanale
            </h2>
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
              Participez au financement de projets innovants qui permettent aux artisanes mauritaniennes 
              de développer leurs activités et de préserver leur savoir-faire traditionnel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {crowdfundingProjects.map((project) => (
              <div
                key={project.id}
                className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-xl`}
              >
                <div className={`w-full h-32 ${project.bgColor} rounded-lg mb-4 flex items-center justify-center`}>
                  <Handshake className="w-12 h-12 text-white" />
                </div>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-blue-600'} mb-2`}>
                  {project.title}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                  {project.description}
                </p>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Progression
                    </span>
                    <span className="text-sm font-bold text-blue-600">
                      {project.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`bg-gradient-to-r ${project.color} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${project.percentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-700'}`}>
                      {project.raised.toLocaleString()} MRU
                    </div>
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      sur {project.goal.toLocaleString()} MRU
                    </div>
                  </div>
                  <button 
                    className={`${project.completed ? 'bg-green-500 hover:bg-green-600' : `bg-gradient-to-r ${project.color}`} text-white px-4 py-2 rounded-full text-sm font-semibold hover:shadow-lg transition-all`}
                    onClick={() => setSelectedProject(project)}
                  >
                    {project.completed ? 'Voir détails' : 'Soutenir'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const FormationsView = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
      <div className="container mx-auto">
        <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8`}>
          Formations & Accompagnement
        </h1>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl`}>
          <h2 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-blue-600'} mb-4`}>
            Développez Vos Compétences Artisanales
          </h2>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
            Découvrez nos programmes de formation conçus pour accompagner les artisanes dans le développement 
            de leurs compétences techniques, commerciales et numériques.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Initiation à la Gestion Financière", icon: "💰" },
              { title: "Marketing Digital", icon: "📱" },
              { title: "Gestion d'Entreprise", icon: "💼" },
              { title: "Design & Innovation", icon: "✨" },
              { title: "E-commerce", icon: "🛒" },
              { title: "Qualité & Standards", icon: "⭐" }
            ].map((formation, i) => (
              <div
                key={i}
                className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6 text-center`}
              >
                <div className="text-4xl mb-4">{formation.icon}</div>
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-blue-600'} mb-2`}>
                  {formation.title}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                  Formation spécialisée pour développer vos compétences.
                </p>
                <button className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  En savoir plus
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const RegisterView = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
      <div className="container mx-auto max-w-md">
        <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8 text-center`}>
          Inscription
        </h1>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl`}>
          <form className="space-y-6">
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Nom complet
              </label>
              <input
                type="text"
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                placeholder="Votre nom complet"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Email
              </label>
              <input
                type="email"
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                placeholder="votre@email.com"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Mot de passe
              </label>
              <input
                type="password"
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                placeholder="••••••••"
              />
            </div>
            
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              S'inscrire
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  const LoginView = () => (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-8`}>
      <div className="container mx-auto max-w-md">
        <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-blue-600'} mb-8 text-center`}>
          Connexion
        </h1>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-8 shadow-xl`}>
          <form className="space-y-6">
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Email
              </label>
              <input
                type="email"
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                placeholder="votre@email.com"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Mot de passe
              </label>
              <input
                type="password"
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
                placeholder="••••••••"
              />
            </div>
            
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Se connecter
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomeView />;
      case 'cart':
        return <CartView />;
      case 'artisans':
        return <ArtisansView />;
      case 'crowdfunding':
        return <CrowdfundingView />;
      case 'formations':
        return <FormationsView />;
      case 'register':
        return <RegisterView />;
      case 'login':
        return <LoginView />;
      case 'contributionProcess':
        return <ContributionProcessView project={selectedProject} />;
      case 'contributionSuccess':
        return <ContributionSuccessView />;
      default:
        return <HomeView />;
    }
  };

  return (
    <div className="min-h-screen font-inter">
      <Header />
      <main>
        {renderPage()}
      </main>
      
      <footer className="bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center p-8 mt-12">
        <div className="container mx-auto">
          <p className="text-lg font-semibold mb-2">
            &copy; 2025 Mauracraft. Tous droits réservés.
          </p>
          <p className="text-sm opacity-80">
            Préservons ensemble le patrimoine artisanal mauritanien
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;


